import AssigningTagMainModal from '@components/AssigningTagMainModal/AssigningTagMainModal';
import DeletInterviewModal from '@components/DeletInterviewModal/DeletInterviewModal';
import FullImageModal from '@components/FullImageModal/FullImageModal';
import ApplicantAvailability from '@features/applicant/components/ApplicantAvailability/ApplicantAvailability';
import { Application } from '@features/applications';
import { formatAddressString } from '@features/applications/components/ApplicantViewApplicantInfo/utils';
import {
  checkInterviewInviteType,
  getStatusChipColor,
  getStatusChipLabel,
} from '@features/applications/components/ApplicationListItem/utils';
import ApplicationStatusBadge from '@features/applications/components/ApplicationStatusBadge/ApplicationStatusBadge';
import { useUpdateApplicationRating } from '@features/applications/hooks/useUpdateApplicationRating';
import { useUpdateEnterpriseApplicationRating } from '@features/applications/hooks/useUpdateEnterpriseApplicationRating';
import { SignedTemplate } from '@features/digitalOnboarding';
import { useGetSignedTemplates } from '@features/digitalOnboarding/hooks/useGetSignedTemplates';
import MessagingModal from '@features/messaging/components/MessagingModal/MessagingModal';
import { getTimeZoneAbbreviation } from '@features/profile';
import { Add, Delete } from '@mui/icons-material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import CloseIcon from '@mui/icons-material/Close';
import EditNoteIcon from '@mui/icons-material/EditNote';
import HomeIcon from '@mui/icons-material/Home';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import {
  Alert,
  alpha,
  Avatar,
  Box,
  Button,
  Grid,
  IconButton,
  Rating,
  Stack,
  Typography,
} from '@mui/material';
import { staticFileLocation } from '@server/constants';
import { Profile, authContext } from '@staffedupusa/shared-components';
import { lightGrey, primaryBlue, primaryPurple, white } from '@styles/colors';
import { medium } from '@styles/fontWeights';
import { formatTimestampIntoRelativeDate } from '@utils/formatTimestamp';
import dayjs from 'dayjs';
import { Dispatch, FC, SetStateAction, useContext, useState } from 'react';
import { Link } from 'react-router-dom';

import ApplicationActionTime from '../ApplicationActionTime/ApplicationActionTime';
import MarkInterviewCTAs from '../MarkInterviewCTAs/MarkInterviewCTAs';

type ApplicantViewApplicantInfoProps = {
  application: Application;
  applicantDetailsData: Profile | null;
  searchValue: string;
  toggleApplicationNotesModal: () => void;
  setApplicationData?: Dispatch<SetStateAction<Application[] | null>>;
  setApplication?: Dispatch<SetStateAction<Application | null>>;
  selectedCompanyId?: string | number;
  updateRating?: (id: number, newRating: number) => void;
};

const ApplicantViewApplicantInfo: FC<ApplicantViewApplicantInfoProps> = ({
  application,
  applicantDetailsData,
  setApplicationData,
  setApplication,
  searchValue,
  selectedCompanyId,
  updateRating,
  toggleApplicationNotesModal,
}) => {
  const browserTimeZone = getTimeZoneAbbreviation();
  const applicationStatusLabel = getStatusChipLabel(
    application.last_action || 'unattended',
    application,
  );

  const { authData } = useContext(authContext);
  const [isAssignTagModalOpen, setIsAssignTagModalOpen] =
    useState<boolean>(false);
  const [isShowDeleteInterviewModal, setIsShowDeleteInterviewModal] =
    useState<boolean>(false);
  const [isMessagingModalOpen, setIsMessagingModalOpen] = useState(false);
  const [image, setImage] = useState<string | null>(null);

  const { updateApplicationRating } = useUpdateApplicationRating({
    searchValue,
  });
  const { updateEnterpriseApplicationRating } =
    useUpdateEnterpriseApplicationRating({
      searchValue,
    });
  const { signedTemplatesData, isSignedTemplatesLoading } =
    useGetSignedTemplates({
      applicationId: application.id,
      enabled: applicationStatusLabel.toLowerCase() === 'hired',
    });
  const signedTemplates: SignedTemplate[] = signedTemplatesData?.object || [];
  let finalInterviewDate = '';

  if (application?.calendar_item) {
    const start = dayjs(application.calendar_item?.dt_start_time);
    const end = dayjs(application.calendar_item?.dt_end_time);
    finalInterviewDate = `${start.format('MMMM DD, YYYY')}`;
  } else if (application?.calendar_block_invite) {
    const { block } = application.calendar_block_invite;
    const { slot } = application.calendar_block_invite;
    const startTime = dayjs(
      slot ? slot.slot_start_time : block?.dt_start_time,
    ).format('MMMM DD, YYYY');
    finalInterviewDate = `${startTime}`;
  }

  const hireflixInterviewCreatedAt =
    application?.hire_flix_interview?.date_created;
  const isFuture = dayjs(hireflixInterviewCreatedAt).isAfter(dayjs());

  return (
    <>
      <Grid
        container
        columnSpacing={{ xs: 0, md: 2 }}
        rowSpacing={{ xs: 2, md: 0 }}
      >
        <Grid
          item
          bgcolor={white}
          py={2}
          px={3}
          borderRadius="8px"
          xs={12}
          md={6}
          display="flex"
          direction="column"
          alignItems="center"
          justifyContent="center"
        >
          <Stack
            spacing={2}
            direction={{ xs: 'column', sm: 'row' }}
            pt={2}
            alignItems="center"
          >
            <Stack spacing={2} alignItems="center">
              <Avatar
                src={(application.applicant?.photo as string) || ''}
                sx={{ width: 140, height: 140 }}
                onClick={() =>
                  setImage((application.applicant.photo as string) || null)
                }
                style={{ borderRadius: 8 }}
              />
              <Button
                variant="contained"
                size="small"
                color="secondary"
                startIcon={<EditNoteIcon />}
                fullWidth
                onClick={toggleApplicationNotesModal}
              >
                Notes
              </Button>
            </Stack>
            <Stack spacing={1}>
              <Box display="flex" alignItems="center">
                {applicationStatusLabel.toLowerCase() === 'hired' &&
                signedTemplates?.length > 0 &&
                signedTemplates.filter((item) => item.file_path).length !==
                  signedTemplates?.length ? (
                  <Box
                    px={2}
                    py={1}
                    bgcolor={alpha(primaryPurple, 0.2)}
                    borderRadius="5px"
                  >
                    <Typography
                      color={primaryPurple}
                      textTransform="capitalize"
                      fontWeight={medium}
                      style={{ fontSize: '13px', lineHeight: '13px' }}
                    >
                      {`${signedTemplates.filter((item) => item.file_path).length}/${signedTemplates?.length} Documents Complete`}
                    </Typography>
                  </Box>
                ) : (
                  <ApplicationStatusBadge
                    label={applicationStatusLabel}
                    color={getStatusChipColor(
                      application.withdrawn
                        ? 'withdraw application'
                        : application.employer_archive
                          ? 'archived'
                          : application.application_actions.hired_action == 1
                            ? 'terminated'
                            : application.application_actions.hired_action == 2
                              ? 'resigned'
                              : application.last_action || 'unattended',
                      application,
                    )}
                  />
                )}
                <Typography
                  color="text.secondary"
                  variant="h5"
                  component="p"
                  ml={1}
                  style={{
                    fontSize: '14px',
                    lineHeight: '12px',
                  }}
                >
                  {application.position}
                </Typography>
              </Box>

              <Stack spacing={1} direction="row" alignItems="center">
                <Typography
                  variant="h3"
                  color="secondary"
                  fontWeight="bold"
                  fontSize="24px"
                  sx={{ wordBreak: 'break-word' }}
                >
                  {application?.applicant?.name}
                </Typography>
                <Typography display="inline-flex">
                  <Rating
                    value={application.rating || null}
                    size="small"
                    onChange={(e, val) => {
                      updateRating && updateRating(application.id, Number(val));
                      authData.user?.enterprise
                        ? updateEnterpriseApplicationRating({
                            rating: Number(val),
                            application_id: application.id,
                            company_id: application.job_listing.company.id,
                          })
                        : updateApplicationRating({
                            rating: Number(val),
                            application_id: application.id,
                          });
                    }}
                  />
                  {Boolean(application.rating) && (
                    <IconButton
                      data-testid="clearRatingButton"
                      sx={{
                        p: 0,
                        backgroundColor: 'black',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '15px',
                        height: '15px',
                        position: 'relative',
                        left: '4px',
                        top: '2px',
                        '&:hover': {
                          backgroundColor: '#EA5757',
                        },
                      }}
                      onClick={() => {
                        updateRating && updateRating(application.id, 0);
                        authData.user?.enterprise
                          ? updateEnterpriseApplicationRating({
                              rating: '0',
                              application_id: application.id,
                              company_id: application.job_listing.company.id,
                            })
                          : updateApplicationRating({
                              rating: 0,
                              application_id: application.id,
                            });
                      }}
                    >
                      <CloseIcon style={{ fontSize: '10px', color: 'white' }} />
                    </IconButton>
                  )}
                </Typography>
              </Stack>
              <Stack flexDirection="row" flexWrap="wrap" gap={1}>
                {application?.tags &&
                  application?.tags?.map(
                    (item: { id: number; tag: string }, index: number) => {
                      return (
                        <Typography
                          style={{
                            backgroundColor: lightGrey,
                            borderRadius: 5,
                            color: 'black',
                            padding: '2px 7px',
                            fontSize: '12px',
                          }}
                        >
                          {item?.tag}
                        </Typography>
                      );
                    },
                  )}
                <Button
                  startIcon={
                    <Add sx={{ fontSize: '16px !important', mr: '-5px' }} />
                  }
                  onClick={() => setIsAssignTagModalOpen(true)}
                  style={{
                    backgroundColor: primaryBlue,
                    color: 'white',
                    borderRadius: 5,
                    padding: '2px 10px',
                    fontSize: '12px',
                  }}
                >
                  Assign Tags
                </Button>
              </Stack>
              {applicantDetailsData?.address1 && (
                <Typography
                  color="text.secondary"
                  fontWeight={medium}
                  style={{ fontSize: '14px' }}
                >
                  <HomeIcon
                    color="primary"
                    fontSize="inherit"
                    sx={{ verticalAlign: 'middle', marginRight: 1 }}
                  />
                  {formatAddressString(applicantDetailsData)}
                </Typography>
              )}

              {applicantDetailsData?.email && (
                <Typography
                  color="text.secondary"
                  fontWeight={medium}
                  style={{ fontSize: '14px', wordBreak: 'break-word' }}
                >
                  <AlternateEmailIcon
                    color="primary"
                    fontSize="inherit"
                    sx={{ verticalAlign: 'middle', marginRight: 1 }}
                  />
                  {applicantDetailsData.email}
                </Typography>
              )}

              {applicantDetailsData?.phone && (
                <Typography
                  color="text.secondary"
                  fontWeight={medium}
                  style={{ fontSize: '14px' }}
                >
                  <LocalPhoneIcon
                    color="primary"
                    fontSize="inherit"
                    sx={{ verticalAlign: 'middle', marginRight: 1 }}
                  />
                  {applicantDetailsData.phone}
                </Typography>
              )}

              <Typography
                color="text.secondary"
                fontWeight={medium}
                style={{ fontSize: '14px' }}
              >
                <ApplicationActionTime
                  application={application}
                  applicationStatusLabel={applicationStatusLabel}
                />
              </Typography>
              <Typography color="text.secondary" style={{ fontSize: '14px' }}>
                Source:{' '}
                {application?.indeed_id ? (
                  <img
                    style={{ width: '40px', alignSelf: 'center' }}
                    src={`${staticFileLocation}img/logos/indeed.png`}
                  />
                ) : application?.application_source === 4 ? (
                  <img
                    style={{
                      width: '60px',
                      marginBottom: '-3px',
                      alignSelf: 'center',
                    }}
                    src={`${staticFileLocation}img/logos/zip-recruiter.jpg`}
                  />
                ) : (
                  'direct'
                )}
              </Typography>

              {application.wotc_credit && (
                <Typography color="success.main" fontWeight="medium">
                  {`WOTC $${application.wotc_credit}`}
                </Typography>
              )}
            </Stack>
          </Stack>

          {application?.hire_flix_interview && (
            <>
              {/* If interview video is NOT submitted */}
              {!application?.hire_flix_interview?.interview_response_url && (
                <Alert
                  sx={{
                    fontSize: '13px',
                    alignSelf: 'start',
                    width: '100%',
                    maxWidth: 'fit-content',
                    margin: '15px auto 0',
                  }}
                  severity="info"
                >
                  Hireflix interview {isFuture ? 'is' : 'was'} created on{' '}
                  <b>
                    {dayjs(
                      application.hire_flix_interview?.date_created,
                    ).format('MMMM DD, YYYY')}{' '}
                    -{' '}
                    {dayjs(
                      application.hire_flix_interview?.date_created,
                    ).format('hh:mm A')}
                  </b>{' '}
                  {browserTimeZone || ''}. <br />
                  Applicant can join the interview at{' '}
                  <b>
                    <Link
                      to={application?.hire_flix_invite_url || '#'}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: '#1c6c8b' }}
                    >
                      {application?.hire_flix_invite_url}
                    </Link>
                  </b>
                </Alert>
              )}

              {/* If interview video IS submitted */}
              {application?.hire_flix_interview?.interview_response_url &&
                (application.hire_flix_interview.interview_status &&
                application.hire_flix_interview.is_visible === false ? (
                  <Alert
                    sx={{
                      marginTop: 3,
                      fontSize: '13px',
                      alignSelf: 'start',
                      width: '100%',
                      maxWidth: 'fit-content',
                      margin: '15px auto 0',
                      backgroundColor: 'rgb(255, 244, 229)',
                      color: 'rgb(102, 60, 0)',
                      '& .MuiAlert-icon': { color: 'rgb(237, 108, 2)' },
                    }}
                    severity="warning"
                  >
                    The applicant has submitted their HireFlix interview, but it
                    is not visible to your account — your HireFlix credits
                    appear to be exhausted. Purchase credits to view the
                    interview response.
                  </Alert>
                ) : (
                  <Alert
                    sx={{
                      marginTop: 3,
                      fontSize: '13px',
                      alignSelf: 'start',
                      width: '100%',
                      maxWidth: 'fit-content',
                      margin: '15px auto 0',
                    }}
                    severity="info"
                  >
                    The applicant has submitted their Hireflix interview.{' '}
                    <b>
                      <Link
                        to={
                          application?.hire_flix_interview
                            ?.interview_response_url || '#'
                        }
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: '#1c6c8b' }}
                      >
                        View Interview Response
                      </Link>
                    </b>
                  </Alert>
                ))}
            </>
          )}
          {(applicationStatusLabel === 'interview requested' ||
            applicationStatusLabel === 'interview no response') &&
          !application?.hire_flix_interview ? (
            <Alert
              sx={{
                marginTop: 3,
                fontSize: '13px',
                alignSelf: 'start',
                width: '100%',
                maxWidth: 'fit-content',
                margin: '15px auto 0',
              }}
              severity="info"
            >
              {application?.calendar_item
                ? 'Interview invite sent for'
                : `Applicant has been invited for an Interview by ${checkInterviewInviteType(application.interview_slots)}.`}{' '}
              {application?.calendar_item && (
                <>
                  <b>
                    {dayjs(application.calendar_item?.dt_start_time).format(
                      'MMMM DD, YYYY',
                    )}
                  </b>{' '}
                  from{' '}
                  <b>
                    {dayjs(application.calendar_item?.dt_start_time).format(
                      'hh:mm A',
                    )}
                  </b>
                  {' - '}
                  <b>
                    {dayjs(application.calendar_item?.dt_end_time).format(
                      'hh:mm A',
                    )}
                  </b>{' '}
                  {browserTimeZone || ''} (awaiting confirmation)
                </>
              )}
            </Alert>
          ) : applicationStatusLabel === 'interview confirmed' ? (
            <Alert
              sx={{
                marginTop: 3,
                fontSize: '13px',
                alignSelf: 'start',
                width: '100%',
                maxWidth: 'fit-content',
                margin: '15px auto 0',
              }}
              severity="info"
            >
              Interview confirmed for{' '}
              {application?.calendar_item ? (
                <>
                  <b>
                    {dayjs(application.calendar_item?.dt_start_time).format(
                      'MMMM DD, YYYY',
                    )}
                  </b>{' '}
                  from{' '}
                  <b>
                    {dayjs(application.calendar_item?.dt_start_time).format(
                      'hh:mm A',
                    )}
                  </b>
                  {' - '}
                  <b>
                    {dayjs(application.calendar_item?.dt_end_time).format(
                      'hh:mm A',
                    )}
                  </b>{' '}
                  {browserTimeZone || ''}
                </>
              ) : application?.calendar_block_invite ? (
                <>
                  <b>
                    {dayjs(
                      application.calendar_block_invite?.block?.dt_start_time,
                    ).format('MMMM DD, YYYY')}
                  </b>{' '}
                  from{' '}
                  <b>
                    {dayjs(
                      application.calendar_block_invite?.slot
                        ? application.calendar_block_invite?.slot
                            ?.slot_start_time
                        : application.calendar_block_invite?.block
                            ?.dt_start_time,
                    ).format('hh:mm A')}
                  </b>
                  {' - '}
                  <b>
                    {dayjs(
                      application.calendar_block_invite?.slot
                        ? application.calendar_block_invite?.slot?.slot_end_time
                        : application.calendar_block_invite?.block?.dt_end_time,
                    ).format('hh:mm A')}
                  </b>{' '}
                  {browserTimeZone || ''}
                </>
              ) : (
                ''
              )}
            </Alert>
          ) : null}
          <Stack
            sx={{ width: '100%' }}
            justifyContent="end"
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            mt={2}
          >
            {applicationStatusLabel === 'interview confirmed' &&
              dayjs(finalInterviewDate).isBefore(dayjs()) && (
                <MarkInterviewCTAs
                  application={application}
                  setApplicationData={setApplicationData}
                  setApplication={setApplication}
                  setIsMessagingModalOpen={setIsMessagingModalOpen}
                />
              )}
            {(applicationStatusLabel === 'interview requested' ||
              applicationStatusLabel === 'interview confirmed' ||
              (application?.hire_flix_interview &&
                !application?.hire_flix_interview?.interview_response_url) ||
              applicationStatusLabel === 'interview no response') && (
              <Button
                variant="outlined"
                size="small"
                sx={{
                  fontSize: '12px',
                  whiteSpace: 'nowrap',
                  width: 'fit-content',
                  alignSelf: 'end',
                  textAlign: 'end',
                }}
                color="error"
                onClick={() => setIsShowDeleteInterviewModal(true)}
              >
                {application?.hire_flix_interview
                  ? 'Delete Hireflix Interview'
                  : 'Delete interview'}
              </Button>
            )}
          </Stack>
        </Grid>
        <Grid item xs={12} md={6} sx={{ overflowX: 'auto' }}>
          <Box
            bgcolor={white}
            p={3}
            borderRadius="8px"
            height="100%"
            justifyContent="center"
            display="flex"
            alignItems="center"
            minWidth={applicantDetailsData?.has_availability ? '500px' : '100%'}
            width="100%"
          >
            {applicantDetailsData?.has_availability ? (
              <ApplicantAvailability applicantDetails={applicantDetailsData} />
            ) : (
              <Typography variant="h5">
                Applicant has not specified availability
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>

      <FullImageModal handleClose={() => setImage(null)} image={image} />
      {isAssignTagModalOpen && (
        <AssigningTagMainModal
          handleClose={() => setIsAssignTagModalOpen(false)}
          applicationId={application.id}
          setApplicationData={setApplicationData}
          setApplication={setApplication}
          selectedCompanyId={selectedCompanyId}
          isAssignTagModalOpen
        />
      )}
      {isShowDeleteInterviewModal && (
        <DeletInterviewModal
          handleClose={() => setIsShowDeleteInterviewModal(false)}
          setApplicationData={setApplicationData}
          setApplication={setApplication}
          selectedCompanyId={selectedCompanyId}
          isShowDeleteInterviewModal
          application={application}
          applicationStatusLabel={applicationStatusLabel}
        />
      )}
      {isMessagingModalOpen && (
        <MessagingModal
          isOpen={isMessagingModalOpen}
          handleClose={() => setIsMessagingModalOpen(false)}
          application={application}
        />
      )}
    </>
  );
};

export default ApplicantViewApplicantInfo;
